import React, { useMemo } from 'react';
import { css } from '@compiled/react';
import ChevronLeftIcon from '@atlaskit/icon/glyph/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Match Salesforce lightning-calendar year range
const MIN_YEAR = 1926;
const MAX_YEAR = 2126;

type Props = {
  value: Date | null;
  viewMonth: number;
  viewYear: number;
  onViewChange: (month: number, year: number) => void;
  onSelect: (date: Date) => void;
};

export const DatePickerCalendar: React.FC<Props> = ({
  value,
  viewMonth,
  viewYear,
  onViewChange,
  onSelect,
}) => {
  const today = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, []);

  const daysInMonth = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const last = new Date(viewYear, viewMonth + 1, 0);
    const startDay = first.getDay();
    const days = last.getDate();
    const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();
    const cells: { date: Date; isCurrentMonth: boolean; isToday: boolean; isSelected: boolean }[] = [];
    const totalCells = 6 * 7; // Always 6 weeks

    for (let i = 0; i < totalCells; i++) {
      let day: number;
      let month = viewMonth;
      let year = viewYear;
      let isCurrentMonth: boolean;

      if (i < startDay) {
        day = prevMonthDays - startDay + i + 1;
        month = viewMonth - 1;
        if (month < 0) {
          month = 11;
          year--;
        }
        isCurrentMonth = false;
      } else if (i < startDay + days) {
        day = i - startDay + 1;
        isCurrentMonth = true;
      } else {
        day = i - startDay - days + 1;
        month = viewMonth + 1;
        if (month > 11) {
          month = 0;
          year++;
        }
        isCurrentMonth = false;
      }

      const date = new Date(year, month, day);
      const isToday =
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate();
      const isSelected = !!(
        value &&
        date.getFullYear() === value.getFullYear() &&
        date.getMonth() === value.getMonth() &&
        date.getDate() === value.getDate()
      );

      cells.push({ date, isCurrentMonth, isToday, isSelected });
    }

    return cells;
  }, [viewMonth, viewYear, today, value]);

  const yearOptions = useMemo(() => {
    const opts: number[] = [];
    for (let y = MIN_YEAR; y <= MAX_YEAR; y++) opts.push(y);
    return opts;
  }, []);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      onViewChange(11, viewYear - 1);
    } else {
      onViewChange(viewMonth - 1, viewYear);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      onViewChange(0, viewYear + 1);
    } else {
      onViewChange(viewMonth + 1, viewYear);
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onViewChange(viewMonth, parseInt(e.target.value, 10));
  };

  const handleToday = () => {
    onSelect(today);
  };

  const formatAriaDate = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const todayCellIndex = daysInMonth.findIndex((c) => c.isToday);

  return (
    <div css={calendarStyles} role="dialog" aria-label={`Date picker: ${MONTH_NAMES[viewMonth]}`}>
      <div css={filterStyles}>
        <div css={monthFilterStyles}>
          <button
            type="button"
            css={navButtonStyles}
            onClick={handlePrevMonth}
            title="Previous Month"
            aria-label="Previous Month"
          >
            <ChevronLeftIcon label="" size="small" />
          </button>
          <h2 css={monthTitleStyles} aria-live="polite">
            {MONTH_NAMES[viewMonth]}
          </h2>
          <button
            type="button"
            css={navButtonStyles}
            onClick={handleNextMonth}
            title="Next Month"
            aria-label="Next Month"
          >
            <ChevronRightIcon label="" size="small" />
          </button>
        </div>
        <div css={yearFilterStyles}>
          <select
            css={yearSelectStyles}
            value={viewYear}
            onChange={handleYearChange}
            aria-label="Pick a Year"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <table css={monthTableStyles} role="grid">
        <thead>
          <tr>
            {DAY_HEADERS.map((d) => (
              <th key={d} scope="col" css={dayHeaderCellStyles}>
                <abbr title={d}>{d}</abbr>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: Math.ceil(daysInMonth.length / 7) }, (_, row) => (
            <tr key={row}>
              {daysInMonth.slice(row * 7, row * 7 + 7).map(({ date, isCurrentMonth, isToday, isSelected }, i) => {
                const cellIndex = row * 7 + i;
                return (
                <td
                  key={i}
                  role="gridcell"
                  css={[
                    dayCellStyles,
                    !isCurrentMonth && dayCellAdjacentMonthStyles,
                    isToday && dayCellTodayStyles,
                    isSelected && dayCellSelectedStyles,
                  ]}
                  aria-selected={isSelected}
                  aria-current={isToday ? 'date' : undefined}
                  aria-label={formatAriaDate(date)}
                  data-value={formatAriaDate(date)}
                >
                  <span
                    role="button"
                    tabIndex={isToday && todayCellIndex === cellIndex ? 0 : -1}
                    css={daySpanStyles}
                    onClick={() => onSelect(date)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(date);
                      }
                    }}
                  >
                    {date.getDate()}
                  </span>
                </td>
              );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <button type="button" css={todayButtonStyles} onClick={handleToday} name="today">
        Today
      </button>
    </div>
  );
};

const calendarStyles = css({
  padding: '12px',
  minWidth: '270px',
  backgroundColor: '#fff',
  borderRadius: '7px',
  boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
});

const filterStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '12px',
  gap: '8px',
});

const monthFilterStyles = css({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  justifyContent: 'space-between',
});

const monthTitleStyles = css({
  margin: 0,
  fontSize: '12px',
  fontWeight: 400,
  color: '#172b4d',
});

const yearFilterStyles = css({
  flexShrink: 0,
});

const navButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderRadius: '3px',
  color: '#42526e',
  ':hover': {
    backgroundColor: '#f4f5f7',
  },
});

// Up and down arrows for year dropdown (SVG data URL)
const yearSelectArrowSvg =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='14' viewBox='0 0 10 14'%3E%3Cpath fill='%2342526e' d='M5 0L2 4h6L5 0z'/%3E%3Cpath fill='%2342526e' d='M5 14l3-4H2l3 4z'/%3E%3C/svg%3E\")";

const yearSelectStyles = css({
  padding: '4px 22px 4px 8px',
  fontSize: '11px',
  border: '1px solid #c9c9c9',
  borderRadius: '4px',
  backgroundColor: '#fff',
  color: '#172b4d',
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage: yearSelectArrowSvg,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 5px center',
  backgroundSize: '8px 11px',
});

const monthTableStyles = css({
  width: '100%',
  borderCollapse: 'collapse',
  marginBottom: '9px',
  border: 'none',
  borderSpacing: 0,
  '& thead': {
    border: 'none',
  },
  '& tbody': {
    border: 'none',
  },
  '& th, & td': {
    border: 'none',
    borderBottom: 'none',
    outline: 'none',
  },
  '& abbr': {
    textDecoration: 'none !important',
    borderBottom: 'none !important',
  },
});

const dayHeaderCellStyles = css({
  fontSize: '11px',
  fontWeight: 600,
  color: '#6b778c',
  textAlign: 'center',
  padding: '4px 0',
  border: 'none',
  '& abbr': {
    textDecoration: 'none',
    borderBottom: 'none',
  },
});

const dayCellStyles = css({
  padding: '4px',
  fontSize: '12px',
  textAlign: 'center',
  verticalAlign: 'middle',
  color: '#172b4d',
  border: 'none',
});

const dayCellAdjacentMonthStyles = css({
  color: '#c1c7d0',
});

const daySpanStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: '30px',
  padding: '4px',
  borderRadius: '4px',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  textDecoration: 'none',
  ':hover': {
    backgroundColor: '#deebff',
  },
});

const dayCellTodayStyles = css({
  '& span': {
    backgroundColor: '#e5e5e5',
    fontWeight: 600,
  },
  '& span:hover': {
    backgroundColor: '#f4f5f7',
  },
});

const dayCellSelectedStyles = css({
  '& span': {
    backgroundColor: '#0052cc',
    color: '#fff',
  },
  '& span:hover': {
    backgroundColor: '#16325c',
  },
});

const todayButtonStyles = css({
  display: 'block',
  width: '100%',
  padding: '8px',
  border: 'none',
  borderTop: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: '11px',
  color: '#0052cc',
  textAlign: 'center',
  textDecoration: 'underline',
  ':hover': {
    color: '#16325c',
  },
});
